import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormFileUpload } from '@ipfabric/ui-components/FormFileUpload/FormFileUpload';
import { Label } from '@ipfabric/ui-components/Label/Label';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { validateAndParseCSV } from './helpers/csvValidator';
import type { CSVParseConfig, ImportError, ImportResult } from './types';

type DataTransferFormProps<T> = {
  title: string;
  existingData: T[];
  exportDescription: string;
  exportButtonText?: string;
  importDescription: string;
  importButtonText?: string;
  importReplaceButtonText?: string;
  downloadExampleText?: string;
  fileFieldName: string;
  onExport: () => Awaitable<void>;
  onDownloadExample: () => void;
  onClose: () => void;
  /**
   * CSV configuration for import validation and parsing
   * Can be a static config or a function that creates config based on replace mode
   */
  csvConfig?:
    | CSVParseConfig<T>
    | ((isReplaceMode: boolean) => CSVParseConfig<T>);
  /**
   * Callback invoked when the import is successful
   */
  onImportSuccess?: (data: T[], replaceEntries: boolean) => void;
};

export const DataTransferForm = <T,>({
  title,
  existingData,
  exportDescription,
  exportButtonText,
  importDescription,
  importButtonText,
  importReplaceButtonText,
  downloadExampleText,
  fileFieldName,
  onExport,
  onDownloadExample,
  csvConfig,
  onImportSuccess,
  onClose,
}: DataTransferFormProps<T>) => {
  const [importLoading, setImportLoading] = useState<LoadingState>('INITIAL');
  const [importResult, setImportResult] = useState<ImportResult<T> | null>(
    null,
  );
  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }, []);

  const { control, formState, setError, clearErrors, setValue } =
    useCustomForm<{
      [K in string]: File | null;
    }>({
      defaultValues: {
        [fileFieldName]: null,
      },
      reValidateMode: 'onChange',
    });

  const uploadedFile = useWatch({
    control,
    name: fileFieldName,
  }) as File | null;
  const fileUploadError = formState.errors[fileFieldName]?.message;

  useEffect(() => {
    if (!uploadedFile) {
      return;
    }

    // Clear previous errors first
    clearErrors(fileFieldName);
    setImportResult(null);
    setImportLoading('INITIAL');

    // Validate file type
    const validMimeTypes = ['text/csv', 'application/vnd.ms-excel'];
    const isValidMimeType = validMimeTypes.includes(uploadedFile.type);
    const isValidExtension = uploadedFile.name.toLowerCase().endsWith('.csv');

    if (!isValidMimeType && !isValidExtension) {
      setError(fileFieldName, {
        type: 'fileType',
        message: 'The uploaded file is not a valid CSV file.',
      });
      return;
    }

    // Validate file size
    const fileSizeLimit =
      typeof csvConfig === 'function'
        ? csvConfig(false).maxFileSizeBytes
        : csvConfig?.maxFileSizeBytes;
    if (fileSizeLimit && uploadedFile.size > fileSizeLimit) {
      setError(fileFieldName, {
        type: 'fileSize',
        message: `File size exceeds the maximum of ${formatFileSize(fileSizeLimit)} allowed.`,
      });
      return;
    }
  }, [
    uploadedFile,
    csvConfig,
    fileFieldName,
    setError,
    clearErrors,
    formatFileSize,
  ]);

  const handleCsvConfigImport = async (
    replaceEntries: boolean,
  ): Promise<ImportResult<T>> => {
    if (!csvConfig) {
      throw new Error('CSV config is required for this import method');
    }

    // Get the appropriate config based on whether csvConfig is a function or static config
    const config =
      typeof csvConfig === 'function' ? csvConfig(replaceEntries) : csvConfig;

    const parseResult = await validateAndParseCSV(uploadedFile, config);

    if (!parseResult.isValid || !parseResult.result) {
      throw new Error(parseResult.error || 'Import failed');
    }

    const result = parseResult.result;

    // Call onImportSuccess only for valid data
    if (onImportSuccess && result.validData.length > 0) {
      await onImportSuccess(result.validData, replaceEntries);
    }

    return result;
  };

  const handleImport = async (replaceEntries: boolean) => {
    try {
      const result = await withLoadingIndicator(setImportLoading)(async () => {
        return await handleCsvConfigImport(replaceEntries);
      });
      setImportResult(result);
      setValue(fileFieldName, null);
    } catch (error) {
      setError(fileFieldName, {
        type: 'import',
        message: error instanceof Error ? error.message : 'Import failed',
      });
    }
  };

  const [exporting, setExporting] = useLoadingState();

  const renderContent = () => (
    <form>
      <Card className='flex flex-col gap p-sm'>
        <div>
          <Label renderAs='div' className='mb-3xs'>
            Export data
          </Label>
          <div className='text-plain text-sm'>{exportDescription}</div>
        </div>
        <Button
          type='button'
          variant='outline'
          loading={exporting === 'LOADING'}
          onClick={() => withLoadingIndicator(setExporting)(onExport)}
          className='flex-1'
          icon='fa-file-export'
          disabled={existingData.length === 0}
        >
          {exportButtonText ?? 'Export data'}
        </Button>
        <div className='border-b border-b-divider' />
        <div>
          <div className='flex justify-between'>
            <Label renderAs='div' className='mb-3xs'>
              Data import
            </Label>
            <Link
              renderAs='button'
              type='button'
              accent='positive'
              size='default'
              onClick={() => onDownloadExample()}
              className='self-start'
            >
              {downloadExampleText ?? 'Download example file'}
            </Link>
          </div>
          <div className='text-plain text-sm'>{importDescription}</div>
        </div>
        <FormFileUpload
          control={control}
          name={fileFieldName}
          key={uploadedFile ? uploadedFile.name : 'empty'}
          className='w-full'
        />
        <div className='flex gap-xs'>
          <Button
            type='button'
            onClick={() => handleImport(false)}
            disabled={
              !uploadedFile ||
              fileUploadError !== undefined ||
              importLoading === 'LOADING'
            }
            variant='primary'
            className='flex-1'
            icon='fa-file-import'
            loading={importLoading === 'LOADING'}
          >
            {importButtonText ?? 'Import data'}
          </Button>
          <Button
            type='button'
            onClick={() => handleImport(true)}
            disabled={
              !uploadedFile ||
              fileUploadError !== undefined ||
              importLoading === 'LOADING'
            }
            variant='primary'
            className='flex-1'
            icon='fa-arrow-right-arrow-left'
            loading={importLoading === 'LOADING'}
          >
            {importReplaceButtonText ?? 'Import & replace'}
          </Button>
        </div>
      </Card>
    </form>
  );

  const renderImportSuccessMessage = (result: ImportResult<T>) => {
    if (result.validData.length > 0) {
      return (
        <Message
          variant='plain'
          type={result.errors.length === 0 ? 'success' : 'warning'}
          title={
            result.errors.length === 0
              ? 'Data imported successfully'
              : 'Data imported partially'
          }
          message={
            <>
              <div>{result.validData.length} rows were added to the table.</div>
              {result.errors.length > 0 && (
                <div>{result.errors.length} rows were not imported.</div>
              )}
            </>
          }
          statusIcon
        />
      );
    }

    return (
      <Message
        variant='plain'
        type='error'
        title='Data not imported'
        message='We could not import any data from the file.'
        statusIcon
      />
    );
  };

  const renderImportErrors = (errors: ImportError[]) => {
    if (errors.length === 0) {
      return null;
    }

    return (
      <div className='mt px'>
        {errors.map(({ id, message }) => (
          <div key={id} className='text-sm text-status-error'>
            <b>{id}:</b>&nbsp;{message}
          </div>
        ))}
      </div>
    );
  };

  const renderImportProcessing = () => {
    if (importLoading === 'INITIAL' && !importResult) {
      return null;
    }

    return (
      <Card className='p'>
        {importLoading === 'LOADING' && (
          <Message
            title='Processing file'
            message='Importing data from the file. Please wait...'
            loading={true}
            variant='plain'
            type='plain'
          />
        )}
        {importLoading === 'LOADED' && importResult && (
          <>
            {renderImportSuccessMessage(importResult)}
            {renderImportErrors(importResult.errors)}
          </>
        )}
        {importLoading === 'ERROR' && (
          <Message
            title='Import failed'
            message='An error occurred while importing the file. Please try again.'
            type='error'
            variant='plain'
          />
        )}
      </Card>
    );
  };

  return (
    <SidePanel title={title} closeOnBackdropClick={false} onClose={onClose}>
      <div className='flex flex-col gap'>
        {renderContent()}
        {(importLoading !== 'INITIAL' || importResult) &&
          renderImportProcessing()}
      </div>
    </SidePanel>
  );
};
